import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, ExternalLink, Check, X, Loader2,
  User,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { mediaUrl } from "@/lib/api";
import { formatDateTime } from "@/lib/dates";

export default function AdminVerifications() {
  const { t } = useTranslation();
  const STATUS_TABS = [
    { value: "in_review", label: t("adminX.verifications.tabInReview") },
    { value: "approved", label: t("adminX.verifications.tabApproved") },
    { value: "rejected", label: t("adminX.verifications.tabRejected") },
  ];
  const [tab, setTab] = useState("in_review");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/verification/admin/queue?status=${tab}&limit=50`);
      setItems(data.requests || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const approve = async (request_id) => {
    setBusy(true);
    try {
      await api.post(`/verification/admin/${request_id}/review`, { approve: true });
      setReviewing(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const reject = async (request_id) => {
    setBusy(true);
    try {
      await api.post(`/verification/admin/${request_id}/review`, {
        approve: false,
        rejection_reason: rejectReason.trim() || t("adminX.verifications.defaultRejection"),
      });
      setReviewing(null);
      setRejectReason("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-verifications-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">
              {t("adminX.verifications.title")}
            </h1>
            <p className="mt-2 text-smoke">{t("adminX.verifications.subtitle")}</p>
          </div>
          <span className="badge" data-testid="queue-count">
            {t("adminX.verifications.inLabel", { n: items.length, label: STATUS_TABS.find((tt) => tt.value === tab)?.label })}
          </span>
        </div>

        <div className="flex gap-2 mb-6" data-testid="status-tabs">
          {STATUS_TABS.map((tt) => (
            <button
              key={tt.value}
              onClick={() => setTab(tt.value)}
              className={`px-4 py-2 rounded-sm font-display text-sm font-semibold transition ${
                tab === tt.value ? "bg-worker text-cream" : "bg-linen text-worker hover:bg-sand"
              }`}
              data-testid={`tab-${tt.value}`}
            >
              {tt.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-smoke">
            <Loader2 className="animate-spin mr-2" /> {t("adminX.verifications.loading")}
          </div>
        ) : items.length === 0 ? (
          <div className="card-base p-12 text-center text-smoke">
            <ShieldCheck size={48} className="mx-auto text-hive-ink mb-3" />
            <p className="font-display text-lg font-semibold text-worker">{t("adminX.verifications.allClear")}</p>
            <p className="text-sm mt-1">{t("adminX.verifications.noneNow", { label: (STATUS_TABS.find((tt) => tt.value === tab)?.label || "").toLowerCase() })}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((v) => (
              <div key={v.request_id} className="card-base p-5" data-testid={`request-${v.request_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-[280px]">
                    {v.user_snippet?.avatar_url ? (
                      <img src={mediaUrl(v.user_snippet.avatar_url)} alt="" className="w-10 h-10 rounded-md object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-sand flex items-center justify-center">
                        <User size={20} className="text-smoke" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-display font-bold text-worker">
                        {v.user_snippet?.full_name || v.user_snippet?.email || v.user_id}
                      </p>
                      {v.user_snippet?.company_name && (
                        <p className="text-sm text-smoke">{v.user_snippet.company_name}</p>
                      )}
                      <p className="text-xs text-smoke font-mono mt-1">
                        {v.user_snippet?.email} · {v.user_snippet?.city || ""}{v.user_snippet?.state ? `, ${v.user_snippet.state}` : ""}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="badge capitalize" data-testid={`method-${v.request_id}`}>
                          {v.method === "stripe_identity" ? t("adminX.verifications.methodStripe") : t("adminX.verifications.methodManual")}
                        </span>
                        <span className="text-xs text-smoke font-mono">
                          {formatDateTime(v.created_at)}
                        </span>
                        {v.method === "manual" && v.document_type && (
                          <span className="text-xs text-smoke">{v.document_type.replace("_", " ")}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {tab === "in_review" && (
                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={() => setReviewing(v)}
                        className="fx-btn fx-btn--secondary text-xs"
                        data-testid={`open-review-${v.request_id}`}
                      >
                        {t("adminX.verifications.reviewDocs")}
                      </button>
                    </div>
                  )}
                  {tab !== "in_review" && (
                    <span className={`badge ${v.status === "approved" ? "badge-verified" : ""}`}>
                      {v.status}
                    </span>
                  )}
                </div>

                {v.notes && tab === "in_review" && (
                  <p className="mt-3 text-sm text-smoke italic">"{v.notes}"</p>
                )}
                {v.rejection_reason && tab === "rejected" && (
                  <p className="mt-2 text-xs text-red-700 font-mono">{v.rejection_reason}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review modal */}
      {reviewing && (
        <div className="fixed inset-0 z-50 bg-worker/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="review-modal">
          <div className="bg-cream rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
            <button onClick={() => { setReviewing(null); setRejectReason(""); }} className="absolute top-3 right-3 text-smoke hover:text-worker">
              <X size={20} />
            </button>
            <h3 className="font-display text-2xl font-bold text-worker mb-1">
              {t("adminX.verifications.modalTitle", { name: reviewing.user_snippet?.full_name || t("adminX.verifications.providerFallback") })}
            </h3>
            <p className="text-sm text-smoke mb-5">
              {reviewing.method === "stripe_identity" ? t("adminX.verifications.stripeBody") : t("adminX.verifications.manualBody")}
            </p>

            {reviewing.method === "manual" && (
              <div className="space-y-4 mb-6">
                <Field label={t("adminX.verifications.fullLegalName")} value={reviewing.full_legal_name} />
                <Field label={t("adminX.verifications.docType")} value={reviewing.document_type?.replace("_", " ")} capitalize />
                <DocLink label={t("adminX.verifications.primaryDoc")} url={reviewing.document_url} />
                {reviewing.selfie_url && <DocLink label={t("adminX.verifications.selfie")} url={reviewing.selfie_url} />}
                {reviewing.business_license_url && <DocLink label={t("adminX.verifications.businessLicense")} url={reviewing.business_license_url} />}
                {reviewing.notes && (
                  <div>
                    <p className="label-base">{t("adminX.verifications.notes")}</p>
                    <p className="text-sm text-worker">"{reviewing.notes}"</p>
                  </div>
                )}
              </div>
            )}

            {reviewing.method === "stripe_identity" && (
              <div className="card-base p-4 mb-6 bg-linen">
                <p className="text-sm text-smoke">
                  {t("adminX.verifications.sessionLabel")} <span className="font-mono text-worker">{reviewing.stripe_identity_session_id}</span>
                </p>
                <p className="text-xs text-smoke mt-2">
                  {t("adminX.verifications.stripeAuto")}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <label className="label-base">{t("adminX.verifications.rejectReason")} <span className="text-smoke normal-case">{t("adminX.verifications.rejectReasonOpt")}</span></label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={2}
                className="input-base resize-none"
                placeholder={t("adminX.verifications.rejectPh")}
                maxLength={400}
                data-testid="rejection-reason-input"
              />
            </div>

            <div className="mt-6 flex gap-2 justify-end flex-wrap">
              <button onClick={() => reject(reviewing.request_id)} disabled={busy} className="fx-btn fx-btn--secondary border-red-500 text-red-700 hover:bg-red-50" data-testid="reject-btn">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <X size={16} />}
                {t("adminX.verifications.rejectBtn")}
              </button>
              <button onClick={() => approve(reviewing.request_id)} disabled={busy} className="fx-btn fx-btn--primary" data-testid="approve-btn">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                {t("adminX.verifications.approveBtn")}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, value, capitalize }) {
  return (
    <div>
      <p className="label-base">{label}</p>
      <p className={`text-sm text-worker ${capitalize ? "capitalize" : ""}`}>{value || "—"}</p>
    </div>
  );
}

function DocLink({ label, url }) {
  return (
    <div>
      <p className="label-base">{label}</p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-hive-ink hover:text-worker font-mono break-all inline-flex items-center gap-1"
        data-testid={`doc-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {url} <ExternalLink size={12} />
      </a>
    </div>
  );
}
